import { useState, useEffect, useCallback, useRef } from "react";
import { getGroupsService, getVipSnapshotService } from "modules/came/services/vipCameService";

const emptyStats = { totalVehicles: 0, parkingVehicles: null, vipCount: null, generalCount: null };
export const useTableVipCame = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewMode, setViewMode] = useState("all");
  const [snapshot, setSnapshot] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [groupError, setGroupError] = useState(null);
  const [dataError, setDataError] = useState(null);
  const mounted = useRef(false);
  const active = useRef(null);
  const groupRequest = useRef(null);
  const revision = useRef(0);
  const loadGroups = useCallback(async () => {
    groupRequest.current?.abort();
    const controller = new AbortController();
    groupRequest.current = controller;
    setGroupsLoading(true);
    setGroupError(null);
    try {
      const result = await getGroupsService(controller.signal);
      if (!mounted.current || controller.signal.aborted) return { canceled: true };
      if (!result.success) throw new Error(result.error || "No se pudieron consultar los grupos");
      setGroups(result.data.filter((group) => group?.idgroup != null && group.name != null));
      return result;
    } catch (error) {
      if (!mounted.current || controller.signal.aborted) return { canceled: true };
      setGroupError(error.message);
      return { success: false, error: error.message };
    } finally {
      if (mounted.current && !controller.signal.aborted) setGroupsLoading(false);
    }
  }, []);
  const loadData = useCallback((group = null) => {
    const key = group == null ? "all" : `group:${group.idgroup}`;
    if (active.current?.key === key && !active.current.controller.signal.aborted)
      return active.current.promise;
    active.current?.controller.abort();
    const controller = new AbortController();
    const current = ++revision.current;
    setDataLoading(true);
    setDataError(null);
    setSnapshot(null);
    const valid = () =>
      mounted.current && !controller.signal.aborted && current === revision.current;
    const promise = (async () => {
      try {
        const result = await getVipSnapshotService(group?.idgroup, controller.signal);
        if (!valid()) return { canceled: true };
        if (!result.success)
          throw new Error(result.error || "No se pudieron consultar los vehículos");
        setSnapshot(result);
        return result;
      } catch (error) {
        if (!valid()) return { canceled: true };
        setDataError(error.message);
        return { success: false, error: error.message };
      } finally {
        if (valid()) {
          setDataLoading(false);
          active.current = null;
        }
      }
    })();
    active.current = { key, controller, promise };
    return promise;
  }, []);
  const loadAllVehiclesData = useCallback(() => {
    setSelectedGroup(null);
    setViewMode("all");
    return loadData();
  }, [loadData]);
  const selectGroup = useCallback(
    (group) => {
      if (group == null) return loadAllVehiclesData();
      if (group.idgroup == null || group.name == null)
        return Promise.resolve({ success: false, error: "Seleccione un grupo válido" });
      setSelectedGroup(group);
      setViewMode("groups");
      return loadData(group);
    },
    [loadData, loadAllVehiclesData]
  );
  const refreshData = useCallback(
    () => loadData(viewMode === "groups" ? selectedGroup : null),
    [loadData, viewMode, selectedGroup]
  );
  useEffect(() => {
    mounted.current = true;
    loadGroups();
    loadAllVehiclesData();
    return () => {
      mounted.current = false;
      revision.current += 1;
      groupRequest.current?.abort();
      active.current?.controller.abort();
      active.current = null;
    };
  }, [loadGroups, loadAllVehiclesData]);
  const groupVehicles = snapshot?.data || [];
  const occupancyKnown = snapshot?.occupancyKnown === true;
  const stats = snapshot
    ? {
        totalVehicles: groupVehicles.length,
        parkingVehicles: occupancyKnown
          ? groupVehicles.filter((vehicle) => vehicle.isInside === true).length
          : null,
        vipCount: occupancyKnown
          ? groupVehicles.filter((vehicle) => vehicle.isInside && vehicle.parkingType === "vip")
              .length
          : null,
        generalCount: occupancyKnown
          ? groupVehicles.filter((vehicle) => vehicle.isInside && vehicle.parkingType === "general")
              .length
          : null,
      }
    : emptyStats;
  const groupQuotasMap = Object.fromEntries(
    groups.map((group) => [group.idgroup, Number(group.quotas) || 0])
  );
  return {
    groups,
    selectedGroup,
    viewMode,
    groupVehicles,
    parkingVehicles: occupancyKnown
      ? groupVehicles.filter((vehicle) => vehicle.isInside === true)
      : [],
    allGroupsVehicles: snapshot?.allData || [],
    currentParkingMap: snapshot?.parkingMap || {},
    cuposInfo: snapshot?.cuposInfo || null,
    stats: { ...stats, groupsCount: groups.length },
    occupancyKnown,
    occupancyWarning: snapshot?.occupancyError || "",
    loading: {
      groups: groupsLoading,
      vehicles: dataLoading,
      parking: false,
      allVehicles: dataLoading,
    },
    isLoading: groupsLoading || dataLoading,
    error: groupError || dataError,
    showNoData: { groupVehicles: false, parkingVehicles: false },
    groupsWithQuotas: groups,
    groupQuotasMap,
    selectedGroupQuota: selectedGroup ? groupQuotasMap[selectedGroup.idgroup] || 0 : 0,
    isAllVehiclesMode: viewMode === "all",
    hasData: groupVehicles.length > 0,
    groupName: selectedGroup?.name || "Todos los Vehículos",
    loadGroups,
    selectGroup,
    refreshData,
    loadAllVehiclesData,
  };
};
